import React, { useContext, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as offerActions from '~/store/actions/loanOfferActions';
import Card from '~/components/shared/Card/Card';
import CardHeader from '~/components/shared/CardHeader/CardHeader';
import CardSection from '~/components/shared/CardSection/CardSection';
import InputField from '~/components/shared/Input/InputField';
import useTranslate from '~/hooks/useTranslate';
import { Offer } from '~/Interfaces';
import { ValidationContext } from '~/components/hoc/withValidation';
import { Typography } from '~/components/shared/Typography/Typography';
import { RootState } from '~/store/types/sharedTypes';
import { CountryCode } from '~/enums';

interface Props {
  data: Offer;
}

const PayoutInfoCard: React.FC<Props> = (props) => {
  const translate = useTranslate();
  const dispatch = useDispatch();
  const validationContext = useContext(ValidationContext);

  const clearingNumberRef = useRef<HTMLInputElement>(null);
  const accountNumberRef = useRef<HTMLInputElement>(null);

  const { market } = useSelector((state: RootState) => state.intl);

  const isSeparateBankAccount = market === CountryCode.DK;

  // store data into redux if user not changed anything
  useEffect(() => {
    dispatch(
      offerActions.updateOfferData({ invoiceEmail: props.data.invoiceEmail })
    );

    dispatch(
      offerActions.updateOfferData({ accountNumber: props.data.accountNumber })
    );
  }, [props.data.accountNumber, props.data.invoiceEmail]);

  const emailHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    validationContext.onChangeHOC(e);
    dispatch(
      offerActions.updateOfferData({ invoiceEmail: e.currentTarget.value })
    );
  };

  const accountNumberHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const prefix = 'BE';
    const { value } = e.currentTarget;
    if (market === CountryCode.BE) {
      e.currentTarget.value = prefix + value.substring(prefix.length);
    }
    validationContext.onChangeHOC(e);
    if (isSeparateBankAccount) {
      const clearingNumber = clearingNumberRef?.current?.value ?? '';
      let accountNumber = accountNumberRef?.current?.value ?? '';
      if (accountNumber.substring(0, 4).includes(clearingNumber)) {
        accountNumber = accountNumber.replace(clearingNumber, '');
      }
      const mergedAccountNumber = clearingNumber + accountNumber;
      dispatch(
        offerActions.updateOfferData({ accountNumber: mergedAccountNumber })
      );
    } else
      dispatch(
        offerActions.updateOfferData({
          accountNumber: e.currentTarget.value.replace(' ', ''),
        })
      );
  };

  const renderBankAccountNumPrefix = () => {
    const prefix = 'BE';
    if (market === CountryCode.BE) {
      if (props.data.accountNumber) {
        return props.data.accountNumber.toUpperCase().includes(prefix)
          ? props.data.accountNumber.toUpperCase()
          : prefix + props.data.accountNumber;
      }
      return prefix;
    }
    return props.data.accountNumber;
  };

  const getSeparateAccountInfo = (): {
    clearingNumber: string;
    accountNumber: string;
  } => ({
    clearingNumber: props.data?.accountNumber?.substring(0, 4) ?? '',
    accountNumber: props.data?.accountNumber?.substring(4) ?? '',
  });

  const renderBankAccountInputs = () => {
    if (isSeparateBankAccount)
      return (
        <>
          <InputField
            type="text"
            name="clearingNumber"
            ref={clearingNumberRef}
            validationType="ClearingNumber"
            placeholder={
              translate(
                'PayoutInfoCard.ClearingNumberInputPlaceholder'
              ) as string
            }
            defaultValue={getSeparateAccountInfo().clearingNumber as string}
            onChange={accountNumberHandler}
            maxLength={4}
            characterCounter
          />
          <InputField
            type="text"
            name="accountNumber"
            ref={accountNumberRef}
            validationType="AccountNumber"
            placeholder={
              translate(
                'PayoutInfoCard.AccountNumberInputPlaceholder'
              ) as string
            }
            defaultValue={getSeparateAccountInfo().accountNumber as string}
            onChange={accountNumberHandler}
          />
        </>
      );
    return (
      <InputField
        type="text"
        name="accountNumber"
        validationType="AccountNumber"
        placeholder={
          translate('PayoutInfoCard.AccountNumberInputPlaceholder') as string
        }
        defaultValue={renderBankAccountNumPrefix()}
        onChange={accountNumberHandler}
      />
    );
  };

  return (
    <Card noPadding dataTestId="loan-offer-payout-info-card">
      <CardHeader value={translate('PayoutInfoCard.Title') as string} />
      <CardSection
        title={translate('PayoutInfoCard.EmailInvoices') as string}
        borderTop
      >
        <InputField
          type="email"
          name="email"
          validationType="Email"
          placeholder={
            translate('PayoutInfoCard.EmailInvoicesInputPlaceholder') as string
          }
          defaultValue={props.data.invoiceEmail}
          onChange={emailHandler}
        />
      </CardSection>
      <CardSection
        title={
          props.data.isLunarPartner
            ? (translate('PayoutInfoCard.LunarAccountTitle') as string)
            : (translate('PayoutInfoCard.BankAccount') as string)
        }
        borderTop
      >
        {props.data.isLunarPartner && (
          <Typography fontSize={0.8} lineHeight={1.3}>
            {translate('PayoutInfoCard.LunarAccountExplanation') as string}
          </Typography>
        )}
        {renderBankAccountInputs()}
      </CardSection>
    </Card>
  );
};

export default PayoutInfoCard;
